import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref, computed } from 'vue'

// Data categories available for filtering
export const DATA_CATEGORIES = [
  'news_media',
  'sec_filings', 
  'financial_news',
  'court_records',
  'sanctions',
  'social_media',
  'press_releases',
  'analyst_reports'
]

// Embedding types available for search
export const EMBEDDING_TYPES = [
  'content',
  'category', 
  'summary',
  'entity',
  'custom_question'
]

// Chat modes
export const CHAT_MODES = {
  BASIC: 'basic',
  RAG_SIMPLE: 'rag-simple',
  RAG_ADVANCED: 'rag-advanced'
}

// Conversation types (matching backend enum values)
export const CONVERSATION_TYPES = {
  BASIC_CHAT: 'basic_chat',
  RAG_CHAT: 'rag_chat', 
  SESSION_RESEARCH: 'session_research'
}

export const useAgentStore = defineStore('agent', () => {
  // State
  const messages = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const currentSessionId = ref(null)
  const currentProjectId = ref(null)
  const currentConversationId = ref(null)
  
  // Conversation management
  const conversations = ref([])
  const currentConversation = ref(null)
  
  // Chat settings
  const chatMode = ref(CHAT_MODES.RAG_ADVANCED) // Default to advanced RAG
  const fontSize = ref('md') // 'sm', 'md', 'lg'
  
  // Computed getters
  const hasMessages = computed(() => messages.value.length > 0)
  const lastMessage = computed(() => messages.value[messages.value.length - 1] || null)
  const isRAGMode = computed(() => chatMode.value !== CHAT_MODES.BASIC)
  const isAdvancedRAG = computed(() => chatMode.value === CHAT_MODES.RAG_ADVANCED)
  const hasConversations = computed(() => conversations.value.length > 0)
  const hasActiveConversation = computed(() => !!currentConversationId.value)
  const isSessionChat = computed(() => {
    // Only true if we have a session AND we're using a session-specific conversation
    return !!currentSessionId.value && 
           (currentConversation.value?.conversationType === CONVERSATION_TYPES.SESSION_RESEARCH || 
            currentConversation.value?.sessionId === currentSessionId.value)
  })
  const conversationType = computed(() => {
    if (currentSessionId.value && isSessionChat.value) return CONVERSATION_TYPES.SESSION_RESEARCH
    return currentConversation.value?.conversationType || CONVERSATION_TYPES.BASIC_CHAT
  })
  
  // Initialize chat for a project/session
  function initializeChat(projectId, sessionId = null) {
    // Clear existing conversation state when switching sessions/projects
    if (currentSessionId.value !== sessionId || currentProjectId.value !== projectId) {
      messages.value = []
      currentConversationId.value = null
      currentConversation.value = null
      conversations.value = []
    }
    
    currentProjectId.value = projectId
    currentSessionId.value = sessionId
    error.value = null
    
    // Load conversations for this session/project
    // Don't automatically force session mode - let user choose
    loadConversations()
  }
  
  // Create a new conversation
  async function createConversation(title = null, conversationType = null) {
    try {
      const payload = {
        sessionId: currentSessionId.value,
        projectId: currentProjectId.value,
        conversationType: conversationType || (currentSessionId.value ? CONVERSATION_TYPES.SESSION_RESEARCH : CONVERSATION_TYPES.BASIC_CHAT),
        title: title || (currentSessionId.value ? 'Research Analysis' : 'New Conversation'),
        useRAG: isRAGMode.value,
        ragSettings: {
          threshold: 0.5, // Updated for direct retrieval system
          limit: 10,
          categories: currentSessionId.value ? ['sec_filings', 'news_media', 'financial_news'] : null,
          embeddingTypes: ['content', 'summary']
        }
      }
      
      const response = await api.post('/chat/conversations', payload)
      
      if (response.data.success) {
        const conversation = response.data.conversation
        currentConversationId.value = conversation.id
        currentConversation.value = conversation
        messages.value = []
        
        // Ensure conversations.value is an array before adding to it
        if (!Array.isArray(conversations.value)) {
          console.warn('conversations.value is not an array, resetting to empty array:', conversations.value)
          conversations.value = []
        }
        
        console.log('Adding conversation to list:', { 
          conversation: conversation.id, 
          currentListLength: conversations.value.length 
        })
        
        // Add to conversations list
        conversations.value.unshift(conversation)
        
        return conversation
      } else {
        throw new Error(response.data.error || 'Failed to create conversation')
      }
    } catch (err) {
      console.error('Error creating conversation:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to create conversation'
      throw err
    }
  }
  
    // Load conversations for current session/project
  async function loadConversations() {
    try {
      const params = new URLSearchParams()
      if (currentSessionId.value) params.append('sessionId', currentSessionId.value)
      if (currentProjectId.value) params.append('projectId', currentProjectId.value)
      params.append('limit', '50')
      
      const response = await api.get(`/chat/conversations?${params}`)
      
      if (response.data.success) {
        // Ensure conversations is always an array
        conversations.value = Array.isArray(response.data.conversations) ? response.data.conversations : []
        
        // Only auto-load session conversation if no conversation is currently active
        // This prevents forcing session mode when user might want general mode
        if (!currentConversationId.value && conversations.value.length > 0) {
          // Load most recent conversation (could be session or general)
          await loadConversation(conversations.value[0].id)
        }
      }
    } catch (err) {
      console.error('Error loading conversations:', err)
      // Don't throw error for loading conversations - just log it
    }
  }
  
  // Load or auto-create session-specific conversation
  async function loadSessionConversation() {
    if (!currentSessionId.value) return
    
    try {
      // First try the session-specific endpoint
      const response = await api.get(`/chat/sessions/${currentSessionId.value}/conversation`)
      
      if (response.data.success) {
        const { conversation, messages: conversationMessages } = response.data
        
        currentConversationId.value = conversation.id
        currentConversation.value = conversation
        messages.value = conversationMessages || []
        
        // Session conversations always use RAG
        chatMode.value = CHAT_MODES.RAG_ADVANCED
        
        return { conversation, messages: conversationMessages }
      } else {
        throw new Error(response.data.error || 'Failed to load session conversation')
      }
    } catch (err) {
      console.error('Error loading session conversation:', err)
      
      // If the session-specific endpoint fails, try to create a new conversation with session context
      try {
        console.log('Falling back to creating new session conversation...')
        const fallbackConversation = await createConversation(
          `Research Session ${currentSessionId.value}`,
          CONVERSATION_TYPES.SESSION_RESEARCH
        )
        
        return { conversation: fallbackConversation, messages: [] }
        
      } catch (fallbackErr) {
        console.error('Fallback conversation creation failed:', fallbackErr)
        error.value = err.response?.data?.error || err.message || 'Failed to load session conversation'
        throw err
      }
    }
  }
  
  // Send message to session conversation (optimized for research)
  async function sendSessionMessage(userMessage) {
    if (!currentSessionId.value) {
      throw new Error('No session ID available for session chat')
    }
    
    // Ensure we have a session conversation loaded
    if (!currentConversationId.value) {
      await loadSessionConversation()
    }
    
    // Add user message to local state immediately
    const userMsg = addMessage({
      role: 'user',
      content: userMessage,
      type: 'text'
    })
    
    isLoading.value = true
    error.value = null
    
    try {
      // Try session-specific endpoint first
      let response
      try {
        response = await api.post(`/chat/sessions/${currentSessionId.value}/conversation/messages`, {
          message: userMessage
        })
      } catch (sessionErr) {
        console.log('Session endpoint failed, falling back to general conversation endpoint...')
        // Fallback to general conversation endpoint
        response = await api.post(`/chat/conversations/${currentConversationId.value}/messages`, {
          message: userMessage,
          useRAG: true,
          sessionId: currentSessionId.value // Pass session context
        })
      }
      
      if (response.data.success) {
        const aiMessage = response.data.message
        
        // Add AI response to local state
        addMessage({
          role: 'assistant',
          content: aiMessage.content,
          type: 'text',
          sources: aiMessage.metadata?.sources || null,
          metadata: aiMessage.metadata || null,
          confidence: aiMessage.metadata?.averageSimilarity || null,
          usedDocuments: true, // Session messages always use RAG
          timestamp: aiMessage.timestamp
        })
        
        return response.data
      } else {
        throw new Error(response.data.error || 'Failed to send session message')
      }
      
    } catch (err) {
      console.error('Session chat error:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to send session message'
      
      // Add error message
      addMessage({
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        type: 'error',
        error: error.value
      })
      
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  // Load a specific conversation
  async function loadConversation(conversationId) {
    try {
      const response = await api.get(`/chat/conversations/${conversationId}?includeMetadata=true`)
      
      if (response.data.success) {
        const { conversation, messages: conversationMessages } = response.data
        
        currentConversationId.value = conversationId
        currentConversation.value = conversation
        messages.value = conversationMessages
        
        // Update chat mode based on conversation settings
        if (conversation.settings?.useRAG !== undefined) {
          chatMode.value = conversation.settings.useRAG ? CHAT_MODES.RAG_ADVANCED : CHAT_MODES.BASIC
        }
        
        return { conversation, messages: conversationMessages }
      } else {
        throw new Error(response.data.error || 'Failed to load conversation')
      }
    } catch (err) {
      console.error('Error loading conversation:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to load conversation'
      throw err
    }
  }
  
  // Update conversation settings
  async function updateConversation(conversationId, updates) {
    try {
      const response = await api.put(`/chat/conversations/${conversationId}`, updates)
      
      if (response.data.success) {
        // Update local conversation data
        if (currentConversationId.value === conversationId) {
          currentConversation.value = { ...currentConversation.value, ...updates }
        }
        
        // Update in conversations list
        const index = conversations.value.findIndex(conv => conv.id === conversationId)
        if (index !== -1) {
          conversations.value[index] = { ...conversations.value[index], ...updates }
        }
        
        return response.data
      } else {
        throw new Error(response.data.error || 'Failed to update conversation')
      }
    } catch (err) {
      console.error('Error updating conversation:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to update conversation'
      throw err
    }
  }
  
  // Delete a conversation
  async function deleteConversation(conversationId) {
    try {
      const response = await api.delete(`/chat/conversations/${conversationId}`)
      
      if (response.data.success) {
        // Remove from conversations list
        conversations.value = conversations.value.filter(conv => conv.id !== conversationId)
        
        // If this was the active conversation, clear it
        if (currentConversationId.value === conversationId) {
          currentConversationId.value = null
          currentConversation.value = null
          messages.value = []
          
          // Load next conversation if available
          if (conversations.value.length > 0) {
            await loadConversation(conversations.value[0].id)
          }
        }
        
        return response.data
      } else {
        throw new Error(response.data.error || 'Failed to delete conversation')
      }
    } catch (err) {
      console.error('Error deleting conversation:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to delete conversation'
      throw err
    }
  }
  
  // Add message to chat
  function addMessage(message) {
    const timestamp = new Date()
    const newMessage = {
      id: `msg_${timestamp.getTime()}`,
      ...message,
      timestamp
    }
    messages.value.push(newMessage)
    return newMessage
  }
  
  // Send user message and get AI response
  async function sendMessage(userMessage, options = {}) {
    if (!userMessage.trim()) return
    
    // Check if user has explicitly chosen session mode or general mode
    if (currentSessionId.value && isSessionChat.value) {
      // User is in session mode - use session-specific chat
      return await sendSessionMessage(userMessage)
    } else {
      // User is in general mode or no session context - use general conversation system
      return await sendGeneralMessage(userMessage, options)
    }
  }
  
  // Send message to general conversation
  async function sendGeneralMessage(userMessage, options = {}) {
    // Create conversation if none exists
    if (!currentConversationId.value) {
      await createConversation()
    }
    
    // Add user message to local state immediately
    const userMsg = addMessage({
      role: 'user',
      content: userMessage,
      type: 'text'
    })
    
    isLoading.value = true
    error.value = null
    
    try {
      // Send message to conversation endpoint
      const response = await api.post(`/chat/conversations/${currentConversationId.value}/messages`, {
        message: userMessage,
        useRAG: options.useRAG || isRAGMode.value
      })
      
      if (response.data.success) {
        const aiMessage = response.data.message
        
        // Add AI response to local state
        addMessage({
          role: 'assistant',
          content: aiMessage.content,
          type: 'text',
          sources: aiMessage.metadata?.sources || null,
          metadata: aiMessage.metadata || null,
          confidence: aiMessage.metadata?.averageSimilarity || null,
          usedDocuments: aiMessage.metadata?.type?.includes('rag') || false,
          timestamp: aiMessage.timestamp
        })
        
        // Update conversation in list with new message count
        const convIndex = conversations.value.findIndex(conv => conv.id === currentConversationId.value)
        if (convIndex !== -1) {
          conversations.value[convIndex].messageCount = response.data.conversation.messageCount
          conversations.value[convIndex].lastActivity = response.data.conversation.lastActivity
          conversations.value[convIndex].lastMessage = {
            role: 'assistant',
            content: aiMessage.content.substring(0, 100) + (aiMessage.content.length > 100 ? '...' : ''),
            timestamp: aiMessage.timestamp
          }
        }
        
        return response.data
      } else {
        throw new Error(response.data.error || 'Failed to send message')
      }
      
    } catch (err) {
      console.error('Chat error:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to send message'
      
      // Add error message
      addMessage({
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        type: 'error',
        error: error.value
      })
      
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  // Send basic message without context
  async function sendBasicMessage(userMessage) {
    const payload = {
      prompt: userMessage
    }
    
    const response = await api.post('/openai/chat', payload)
    return response.data
  }
  
  // Send simple RAG message with basic context
  async function sendSimpleRAGMessage(userMessage, options = {}) {
    const payload = {
      prompt: userMessage,
      useDocuments: options.useDocuments !== false // Default to true
    }
    
    const response = await api.post('/openai/rag-chat', payload)
    return response.data
  }
  
  // Send advanced RAG message with full context and source attribution
  // Note: Uses direct retrieval system for reliable results
  async function sendAdvancedRAGMessage(userMessage, options = {}) {
    const payload = {
      question: userMessage,
      sessionId: currentSessionId.value,
      categories: options.categories || null,
      embeddingTypes: options.embeddingTypes || ['content', 'summary'],
      limit: options.limit || 8,
      threshold: options.threshold || 0.5 // Updated for direct retrieval system
    }
    
    const response = await api.post('/rag/query', payload)
    
    // Check for success flag in advanced RAG responses
    if (response.data.success === false) {
      throw new Error(response.data.error || 'RAG query failed')
    }
    
    return response.data
  }
  
  // Upload file and add to chat
  async function uploadFile(file, options = {}) {
    return // not using yet
    if (!file) return
    
    // Add file upload message
    const fileMsg = addMessage({
      role: 'user',
      content: `Uploaded file: ${file.name}`,
      type: 'file',
      file: {
        name: file.name,
        size: file.size,
        type: file.type
      }
    })
    
    isLoading.value = true
    error.value = null
    
    try {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append('file', file)
      if (currentProjectId.value) formData.append('projectId', currentProjectId.value)
      if (currentSessionId.value) formData.append('sessionId', currentSessionId.value)
      
      // Upload file to research store
      const response = await api.post('/research/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      // Add success response
      addMessage({
        role: 'assistant',
        content: `File "${file.name}" has been uploaded and indexed. You can now ask questions about its content.`,
        type: 'file_success',
        uploadedDocument: response.data
      })
      
      return response.data
      
    } catch (err) {
      console.error('File upload error:', err)
      error.value = err.response?.data?.error || 'Failed to upload file'
      
      // Add error message
      addMessage({
        role: 'assistant',
        content: `Failed to upload file "${file.name}". Please try again.`,
        type: 'file_error',
        error: error.value
      })
      
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  // Search documents without generating response
  async function searchDocuments(query, limit = 5) {
    try {
      const response = await api.post('/rag/search', {
        query,
        limit
      })
      return response.data
    } catch (err) {
      console.error('Document search error:', err)
      throw err
    }
  }
  
  // Set chat mode
  function setChatMode(mode) {
    if (Object.values(CHAT_MODES).includes(mode)) {
      chatMode.value = mode
    }
  }
  
  // Toggle between basic and advanced RAG
  function toggleRAG() {
    // If we're in a session context, allow toggling session mode on/off
    if (currentSessionId.value) {
      toggleSessionMode()
    } else {
      // Normal RAG toggle for non-session contexts
      if (chatMode.value === CHAT_MODES.BASIC) {
        chatMode.value = CHAT_MODES.RAG_ADVANCED
      } else {
        chatMode.value = CHAT_MODES.BASIC
      }
    }
  }
  
  // Toggle session-specific vs general chat mode
  function toggleSessionMode() {
    if (!currentSessionId.value) return // No session to toggle
    
    if (isSessionChat.value) {
      // Switch to general project chat (clear session conversation)
      currentConversationId.value = null
      currentConversation.value = null
      messages.value = []
      
      // Load general project conversations instead
      loadGeneralConversations()
    } else {
      // Switch back to session-specific chat
      loadSessionConversation()
    }
  }
  
  // Load general (non-session) conversations for the project
  async function loadGeneralConversations() {
    try {
      const params = new URLSearchParams()
      if (currentProjectId.value) params.append('projectId', currentProjectId.value)
      // Don't include sessionId to get general project conversations
      params.append('limit', '50')
      
      const response = await api.get(`/chat/conversations?${params}`)
      
      if (response.data.success) {
        // Ensure conversations is always an array before filtering
        const allConversations = Array.isArray(response.data.conversations) ? response.data.conversations : []
        
        // Filter out session-specific conversations on the frontend for now
        const generalConversations = allConversations.filter(conv => 
          conv.conversationType !== CONVERSATION_TYPES.SESSION_RESEARCH && !conv.sessionId
        )
        
        conversations.value = generalConversations
        
        // Load most recent general conversation if available
        if (generalConversations.length > 0) {
          await loadConversation(generalConversations[0].id)
        } else {
          // No general conversations exist, create one
          await createConversation('General Chat', CONVERSATION_TYPES.BASIC_CHAT)
        }
      }
    } catch (err) {
      console.error('Error loading general conversations:', err)
      // Don't throw error - just log it
    }
  }
  
  // Set font size
  function setFontSize(size) {
    fontSize.value = size
  }
  
  // Clear current chat and start new conversation
  async function clearChat() {
    messages.value = []
    error.value = null
    currentConversationId.value = null
    currentConversation.value = null
  }
  
  // Start a new conversation
  async function startNewConversation(title = null) {
    await clearChat()
    return await createConversation(title)
  }
  
  // Delete specific message
  function deleteMessage(messageId) {
    const index = messages.value.findIndex(msg => msg.id === messageId)
    if (index !== -1) {
      messages.value.splice(index, 1)
    }
  }
  
  // Regenerate last AI response
  async function regenerateLastResponse() {
    const lastUserMessage = [...messages.value].reverse().find(msg => msg.role === 'user')
    if (!lastUserMessage) return
    
    // Remove last AI response if it exists
    if (lastMessage.value?.role === 'assistant') {
      messages.value.pop()
    }
    
    // Resend the last user message
    return await sendMessage(lastUserMessage.content)
  }
  
  // Get available data categories for filtering
  function getDataCategories() {
    return DATA_CATEGORIES
  }
  
  // Get available embedding types
  function getEmbeddingTypes() {
    return EMBEDDING_TYPES
  }
  
  // Get chat modes
  function getChatModes() {
    return CHAT_MODES
  }
  
  // Get conversation types
  function getConversationTypes() {
    return CONVERSATION_TYPES
  }
  
  // Get chat context for continuing conversation
  function getChatContext(messageCount = 5) {
    return messages.value
      .slice(-messageCount)
      .map(msg => `${msg.role}: ${msg.content}`)
      .join('\n')
  }
  
  // Switch to a different session (clears current conversation state)
  async function switchSession(projectId, sessionId = null) {
    // Clear all conversation state immediately
    messages.value = []
    currentConversationId.value = null
    currentConversation.value = null
    conversations.value = []
    error.value = null
    isLoading.value = false
    
    // Update session/project context
    currentProjectId.value = projectId
    currentSessionId.value = sessionId
    
    // Load new session's conversations
    await loadConversations()
  }
  
  return {
    // State
    messages,
    isLoading,
    error,
    currentSessionId,
    currentProjectId,
    currentConversationId,
    conversations,
    currentConversation,
    chatMode,
    fontSize,
    
    // Computed
    hasMessages,
    lastMessage,
    isRAGMode,
    isAdvancedRAG,
    hasConversations,
    hasActiveConversation,
    isSessionChat,
    conversationType,
    
    // Actions
    initializeChat,
    switchSession,
    addMessage,
    sendMessage,
    sendGeneralMessage,
    sendSessionMessage,
    sendBasicMessage,
    sendSimpleRAGMessage,
    sendAdvancedRAGMessage,
    uploadFile,
    searchDocuments,
    setChatMode,
    toggleRAG,
    toggleSessionMode,
    setFontSize,
    clearChat,
    startNewConversation,
    deleteMessage,
    regenerateLastResponse,
    getDataCategories,
    getEmbeddingTypes,
    getChatModes,
    getConversationTypes,
    getChatContext,
    
    // Conversation Management
    createConversation,
    loadConversations,
    loadConversation,
    loadSessionConversation,
    updateConversation,
    deleteConversation
  }
})